import { DocumentNode, useApolloClient, useQuery } from '@apollo/client';
import { faChevronDown } from '@fortawesome/pro-regular-svg-icons';
import { isEqual } from 'lodash';
import * as React from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import {
    buildFieldSettingsOverview,
    formatOptionValue,
    isUsedIn,
    OverviewOption,
} from './fieldSettingsOverviewData.ts';
import { FieldDefinition, getFieldLabel, getOptionLabel } from './fieldsConfigData.ts';
import Button from '../../core/components/Button.tsx';
import DropdownMenu from '../../core/components/DropdownMenu.tsx';
import { withIcon } from '../../core/components/Icon.tsx';
import { SettingsCard } from '../../core/components/SettingsFormCard.tsx';
import Switch from '../../core/components/Switch.tsx';
import classnames from '../../core/utils/classnames.tsx';

const ChevronDownIcon = withIcon(faChevronDown);

const DEFAULT_COMPARISON = 'default';

interface Channel {
    id: string;
    name: string;
}

interface Comparison {
    label: string;
    fieldsByName: Map<string, FieldDefinition> | null;
}

const rowClass = (isLast: boolean, difference: string | null) =>
    classnames(
        'flex gap-3 pl-3.5 pr-4 border-l-2 border-solid',
        difference ? 'border-l-line-warning-strong' : 'border-l-transparent',
        !isLast && 'border-b border-b-line-weak'
    );

const LineNumber = ({ number, className }: { number: string; className?: string }) => (
    <span className={classnames('w-13 flex-none text-xs text-content-weak tabular-nums', className)}>{number}</span>
);

const Difference = ({ children }: { children: React.ReactNode }) => (
    <span className="flex-none text-xs text-content-medium">{children}</span>
);

interface GroupRowProps extends React.ComponentPropsWithRef<'div'> {
    number: string;
    label: string;
    isUsed: boolean;
    isLast: boolean;
    difference: string | null;
}

const GroupRow = ({ number, label, isUsed, isLast, difference, className, ...props }: GroupRowProps) => {
    const { t } = useTranslation('config');
    return (
        <div
            {...props}
            className={classnames(rowClass(isLast, difference), 'items-baseline bg-surface-medium py-1.5', className)}
        >
            <LineNumber number={number} />
            <span
                className={classnames(
                    'flex-1 min-w-0 truncate text-xs',
                    isUsed ? 'text-content-medium' : 'text-content-weak'
                )}
            >
                {label}
                {!isUsed && ` · ${t('configPage.document.overview.notUsed')}`}
            </span>
            {difference && <Difference>{difference}</Difference>}
        </div>
    );
};

interface OptionRowProps {
    option: OverviewOption;
    fieldKey: string;
    path: string[];
    isLast: boolean;
    difference: string | null;
}

const OptionRow = ({ option, fieldKey, path, isLast, difference }: OptionRowProps) => {
    const { t } = useTranslation('config');
    const label = [...path, getOptionLabel(t, fieldKey, option.name)].join(' › ');
    const value = formatOptionValue(option.name, option.value, t);
    return (
        <div className={classnames(rowClass(isLast, difference), 'py-2')}>
            <LineNumber number={option.number} className="pt-0.5" />
            <span className="flex flex-col flex-1 min-w-0">
                <span className="truncate text-xs text-content-medium" title={label}>
                    {label}
                </span>
                <span className="flex items-baseline justify-between gap-3 min-w-0">
                    <span
                        className={classnames(
                            'truncate text-sm',
                            difference ? 'font-medium text-content-strong' : 'text-content-medium'
                        )}
                        title={value}
                    >
                        {value}
                    </span>
                    {difference && <Difference>{difference}</Difference>}
                </span>
            </span>
        </div>
    );
};

interface CompareToMenuProps {
    value: string;
    channels: Channel[];
    onChange: (value: string) => void;
}

const CompareToMenu = ({ value, channels, onChange }: CompareToMenuProps) => {
    const { t } = useTranslation('config');
    const current = channels.find((channel) => channel.id === value);
    return (
        <span className="flex items-center gap-2 text-sm text-content-medium">
            {t('configPage.document.overview.compareTo')}
            <DropdownMenu>
                <DropdownMenu.Trigger asChild>
                    <Button type="button" className="inline-flex items-center gap-2">
                        {current?.name ?? t('configPage.document.overview.defaultSettings')}
                        <ChevronDownIcon />
                    </Button>
                </DropdownMenu.Trigger>
                <DropdownMenu.Content align="end" className="max-h-80">
                    <DropdownMenu.RadioGroup value={value} onValueChange={onChange}>
                        <DropdownMenu.RadioItem value={DEFAULT_COMPARISON}>
                            {t('configPage.document.overview.defaultSettings')}
                        </DropdownMenu.RadioItem>
                        {channels.length > 0 && <DropdownMenu.Separator />}
                        {channels.map((channel) => (
                            <DropdownMenu.RadioItem key={channel.id} value={channel.id}>
                                {channel.name}
                            </DropdownMenu.RadioItem>
                        ))}
                    </DropdownMenu.RadioGroup>
                </DropdownMenu.Content>
            </DropdownMenu>
        </span>
    );
};

interface FieldSettingsOverviewProps {
    fields: FieldDefinition[];
    translationPrefix: string;
    comparison: Comparison;
    isOnlyDifferences: boolean;
}

const FieldSettingsOverview = ({
    fields,
    translationPrefix,
    comparison,
    isOnlyDifferences,
}: FieldSettingsOverviewProps) => {
    const { t } = useTranslation('config');
    const overview = useMemo(() => buildFieldSettingsOverview(fields), [fields]);
    const labelOf = (key: string) => getFieldLabel(t, key, fields, undefined, translationPrefix);

    const optionDifference = (option: OverviewOption, key: string) => {
        if (!comparison.fieldsByName) {
            return option.isChanged
                ? t('configPage.document.overview.defaultValue', {
                      value: formatOptionValue(option.name, option.defaultValue, t),
                  })
                : null;
        }
        // a field the other channel doesn't use is marked once on its own row
        if (!isUsedIn(comparison.fieldsByName, key)) {
            return null;
        }
        const otherValue = comparison.fieldsByName
            .get(key)
            ?.options?.find((other) => other.name === option.name)?.value;
        return isEqual(option.value ?? null, otherValue ?? null)
            ? null
            : t('configPage.document.overview.otherValue', {
                  channel: comparison.label,
                  value: formatOptionValue(option.name, otherValue, t),
              });
    };
    const usageDifference = (key: string, isUsed: boolean) => {
        if (!comparison.fieldsByName) {
            return null;
        }
        const isOtherUsed = isUsedIn(comparison.fieldsByName, key);
        return isOtherUsed === isUsed
            ? null
            : t('configPage.document.overview.otherValue', {
                  channel: comparison.label,
                  value: t(`configPage.document.overview.${isOtherUsed ? 'used' : 'notUsed'}`),
              });
    };
    // settings of unused fields are left out; their numbers stay reserved, so numbers still match other channels
    const optionsOf = (options: OverviewOption[], key: string, isUsed: boolean) =>
        (isUsed ? options : [])
            .map((option) => ({ option, difference: optionDifference(option, key) }))
            .filter(({ difference }) => !isOnlyDifferences || difference);

    const sections = overview
        .map((field) => {
            const label = labelOf(field.key);
            const difference = usageDifference(field.key, field.isUsed);
            const options = optionsOf(field.options, field.key, field.isUsed);
            const subfields = (field.isUsed ? field.subfields : [])
                .map((subfield) => {
                    const subfieldDifference = usageDifference(subfield.key, subfield.isUsed);
                    const subfieldOptions = optionsOf(subfield.options, subfield.key, subfield.isUsed);
                    return {
                        subfield,
                        path: [label, labelOf(subfield.key)],
                        difference: subfieldDifference,
                        options: subfieldOptions,
                        isShown: !isOnlyDifferences || !!subfieldDifference || subfieldOptions.length > 0,
                    };
                })
                .filter((subfield) => subfield.isShown);
            const isShown = !isOnlyDifferences || !!difference || options.length > 0 || subfields.length > 0;
            return { field, label, difference, options, subfields, isShown };
        })
        .filter((section) => section.isShown);

    return (
        <>
            {sections.length === 0 ? (
                <p className="text-sm text-content-medium">{t('configPage.document.overview.noDifferences')}</p>
            ) : (
                <div className="rounded-lg border border-solid border-line-weak bg-surface-weak">
                    {sections.map((section, sectionIndex) => {
                        const rows: ((isLast: boolean) => React.ReactNode)[] = [
                            ...section.options.map(({ option, difference }) => (isLast: boolean) => (
                                <OptionRow
                                    key={option.number}
                                    option={option}
                                    fieldKey={section.field.key}
                                    path={[section.label]}
                                    isLast={isLast}
                                    difference={difference}
                                />
                            )),
                            ...section.subfields.flatMap(({ subfield, path, difference, options }) => [
                                (isLast: boolean) => (
                                    <GroupRow
                                        key={subfield.number}
                                        number={subfield.number}
                                        label={path.join(' › ')}
                                        isUsed={subfield.isUsed}
                                        isLast={isLast}
                                        difference={difference}
                                    />
                                ),
                                ...options.map(({ option, difference: optionDifference }) => (isLast: boolean) => (
                                    <OptionRow
                                        key={option.number}
                                        option={option}
                                        fieldKey={subfield.key}
                                        path={path}
                                        isLast={isLast}
                                        difference={optionDifference}
                                    />
                                )),
                            ]),
                        ];
                        const isLastSection = sectionIndex === sections.length - 1;
                        // one section per field, so the next field's header pushes the previous one out
                        return (
                            <section key={section.field.number}>
                                <GroupRow
                                    number={section.field.number}
                                    label={section.label}
                                    isUsed={section.field.isUsed}
                                    isLast={isLastSection && rows.length === 0}
                                    difference={section.difference}
                                    // -top-7 cancels Page.Content's padding
                                    className={classnames('sticky -top-7 z-[5]', sectionIndex === 0 && 'rounded-t-lg')}
                                />
                                {rows.map((row, index) => row(isLastSection && index === rows.length - 1))}
                            </section>
                        );
                    })}
                </div>
            )}
        </>
    );
};

// what All settings needs of a product's configuration to list the other channels and load one to compare to
export interface OverviewDocumentConfiguration {
    documentTypeName: string;
    getOverviewData: DocumentNode;
    documentQueries: { GET_CHANNEL_CONFIG: DocumentNode };
}

interface FieldsAllSettingsCardProps {
    // like "Header settings"
    title: string;
    translationPrefix: string;
    // the section's fields of a channel
    selectFields: (fields: FieldDefinition[]) => FieldDefinition[];
    config: { fields?: FieldDefinition[] } | undefined;
    documentConfiguration: OverviewDocumentConfiguration;
    channelId: string;
    onBack: () => void;
}

// every setting of a section's fields, compared to the defaults or another channel
export const FieldsAllSettingsCard = ({
    title,
    translationPrefix,
    selectFields,
    config,
    documentConfiguration,
    channelId,
    onBack,
}: FieldsAllSettingsCardProps) => {
    const { t } = useTranslation('config');
    const client = useApolloClient();
    const [isOnlyDifferences, setOnlyDifferences] = useState(false);
    // opened from a section further down the list, the view starts at the top: the page's scroll area is scrolled up
    const cardRef = useRef<HTMLElement>(null);
    useEffect(() => {
        let scrollArea = cardRef.current?.parentElement;
        while (scrollArea && scrollArea.scrollHeight <= scrollArea.clientHeight) scrollArea = scrollArea.parentElement;
        scrollArea?.scrollTo({ top: 0 });
    }, []);
    // rows keep the previous comparison until the picked channel has loaded, so they don't flash the defaults
    const [selectedId, setSelectedId] = useState(DEFAULT_COMPARISON);
    const [compareChannel, setCompareChannel] = useState(null);
    const latestRequest = useRef(DEFAULT_COMPARISON);
    const { data: channelsData } = useQuery(documentConfiguration.getOverviewData);
    const channels: Channel[] = (Object.values(channelsData ?? {}).find(Array.isArray) ?? []).filter(
        (channel: Channel) => channel.id !== channelId
    );
    const select = async (id: string) => {
        setSelectedId(id);
        latestRequest.current = id;
        if (id === DEFAULT_COMPARISON) {
            setCompareChannel(null);
            return;
        }
        try {
            const { data } = await client.query({
                query: documentConfiguration.documentQueries.GET_CHANNEL_CONFIG,
                variables: { id },
                fetchPolicy: 'network-only',
            });
            if (latestRequest.current === id) {
                setCompareChannel(data?.[`${documentConfiguration.documentTypeName}ProcessingChannel`] ?? null);
            }
        } catch (e) {
            console.error(e);
        }
    };
    const isComparingChannel = selectedId !== DEFAULT_COMPARISON && !!compareChannel;
    const compareFields: FieldDefinition[] | null = isComparingChannel
        ? selectFields(compareChannel.documentConfig?.fields || [])
        : null;

    return (
        <SettingsCard
            ref={cardRef}
            className="gap-8"
            title={title}
            controls={
                <Button type="button" variant="outline" onClick={onBack}>
                    {t('configPage.document.overview.backToFields')}
                </Button>
            }
        >
            <div className="flex flex-wrap items-center justify-between gap-3">
                <label className="flex items-center gap-2 text-sm text-content-strong">
                    <Switch value={isOnlyDifferences} onValueChange={setOnlyDifferences} />
                    {t('configPage.document.overview.onlyDifferences')}
                </label>
                <CompareToMenu value={selectedId} channels={channels} onChange={select} />
            </div>
            <FieldSettingsOverview
                fields={selectFields(config?.fields || [])}
                translationPrefix={translationPrefix}
                comparison={{
                    label: isComparingChannel ? compareChannel.name : t('configPage.document.overview.defaultSettings'),
                    fieldsByName: compareFields && new Map(compareFields.map((field) => [field.name, field])),
                }}
                isOnlyDifferences={isOnlyDifferences}
            />
        </SettingsCard>
    );
};
